import { getStorageData, setStorageData } from "@/utils/storageUtil";

export const CREDENTIAL_METADATA_PROP = "credentialMetadata";
export const LEGACY_CREDENTIAL_PROP = "credentials";
export const BACKGROUND_FALLBACK_PROP = "credentialBackgroundsFallback";
export const CREDENTIAL_SCHEMA_VERSION = 2;
export const MIN_NORMALIZED_NATIVE_VERSION = 29;

const DB_NAME = "omint-credential-assets";
const DB_VERSION = 1;
const BACKGROUND_STORE = "credential-backgrounds";

export type BackgroundSourceType = "data-url" | "url";

export interface CredentialBackgroundSource {
  key: string;
  sourceType: BackgroundSourceType;
  mimeType: string;
  source: string;
}

interface StoredCredentialBackground {
  key: string;
  sourceType: BackgroundSourceType;
  mimeType: string;
  blob?: Blob;
  url?: string;
  updatedAt: number;
}

export interface NormalizedCredentialBundle {
  schemaVersion: number;
  revision: string;
  credentials: any[];
  backgrounds: CredentialBackgroundSource[];
  storage: "indexeddb" | "localstorage";
}

let databasePromise: Promise<IDBDatabase> | null = null;
let objectUrls: Record<string, string> = {};

const openDatabase = (): Promise<IDBDatabase> => {
  if (!globalThis.indexedDB) return Promise.reject(new Error("IndexedDB indisponível"));
  if (databasePromise) return databasePromise;

  databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(BACKGROUND_STORE)) {
        database.createObjectStore(BACKGROUND_STORE, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Não foi possível abrir o IndexedDB"));
    request.onblocked = () => reject(new Error("A abertura do IndexedDB foi bloqueada"));
  }).catch(error => {
    databasePromise = null;
    throw error;
  });

  return databasePromise;
};

const requestResult = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Falha na operação do IndexedDB"));
  });

const waitForTransaction = (transaction: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Falha na transação do IndexedDB"));
    transaction.onabort = () => reject(transaction.error ?? new Error("Transação do IndexedDB cancelada"));
  });

const dataUrlToBlob = (source: string): { blob: Blob; mimeType: string } => {
  const separator = source.indexOf(",");
  if (separator < 0) throw new Error("Fundo Base64 inválido");
  const metadata = source.substring(0, separator);
  const mimeType = metadata.match(/^data:([^;,]+)/)?.[1] ?? "application/octet-stream";
  const encoded = source.substring(separator + 1);
  const binary = metadata.includes(";base64") ? globalThis.atob(encoded) : decodeURIComponent(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
  return { blob: new Blob([bytes], { type: mimeType }), mimeType };
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("Não foi possível ler o fundo"));
    reader.readAsDataURL(blob);
  });

const fallbackSha256 = (value: string): string => {
  const encoded = encodeURIComponent(value);
  const bytes: number[] = [];
  for (let index = 0; index < encoded.length; index++) {
    if (encoded[index] === "%") {
      bytes.push(Number.parseInt(encoded.substring(index + 1, index + 3), 16));
      index += 2;
    } else {
      bytes.push(encoded.charCodeAt(index));
    }
  }
  const bitLength = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  for (let shift = 56; shift >= 0; shift -= 8) bytes.push(Math.floor(bitLength / 2 ** shift) & 0xff);

  const words = new Uint32Array(64);
  const hash = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
  ]);
  const constants = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98,
    0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8,
    0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819,
    0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
    0xc67178f2
  ]);
  const rotateRight = (word: number, bits: number) => (word >>> bits) | (word << (32 - bits));

  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let index = 0; index < 16; index++) {
      const start = offset + index * 4;
      words[index] = (bytes[start] << 24) | (bytes[start + 1] << 16) | (bytes[start + 2] << 8) | bytes[start + 3];
    }
    for (let index = 16; index < 64; index++) {
      const s0 = rotateRight(words[index - 15], 7) ^ rotateRight(words[index - 15], 18) ^ (words[index - 15] >>> 3);
      const s1 = rotateRight(words[index - 2], 17) ^ rotateRight(words[index - 2], 19) ^ (words[index - 2] >>> 10);
      words[index] = (words[index - 16] + s0 + words[index - 7] + s1) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = hash;
    for (let index = 0; index < 64; index++) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temporary1 = (h + sum1 + choice + constants[index] + words[index]) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temporary2 = (sum0 + majority) >>> 0;
      [h, g, f, e, d, c, b, a] = [g, f, e, (d + temporary1) >>> 0, c, b, a, (temporary1 + temporary2) >>> 0];
    }
    [a, b, c, d, e, f, g, h].forEach((word, index) => (hash[index] = (hash[index] + word) >>> 0));
  }
  return Array.from(hash)
    .map(word => word.toString(16).padStart(8, "0"))
    .join("");
};

export async function sha256(value: string): Promise<string> {
  try {
    if (!globalThis.crypto?.subtle) return fallbackSha256(value);
    const data = new TextEncoder().encode(value);
    const digest = await globalThis.crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(digest))
      .map(byte => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return fallbackSha256(value);
  }
}

const resolveLegacyBackground = (credentials: any[], credential: any): string => {
  if (credential?.bg) return credential.bg;
  let position = Number(credential?.posBg);
  const visited = new Set<number>();
  while (Number.isInteger(position) && position >= 0 && position < credentials.length && !visited.has(position)) {
    visited.add(position);
    const referenced = credentials[position];
    if (referenced?.bg) return referenced.bg;
    position = Number(referenced?.posBg);
  }
  return "";
};

const backgroundSource = async (source: string): Promise<CredentialBackgroundSource> => {
  const sourceType: BackgroundSourceType = source.startsWith("data:") ? "data-url" : "url";
  const mimeType = sourceType === "data-url" ? source.match(/^data:([^;,]+)/)?.[1] ?? "application/octet-stream" : "";
  return { key: await sha256(source), sourceType, mimeType, source };
};

export async function normalizeCredentials(credentials: any[] = []) {
  const backgroundsBySource = new Map<string, CredentialBackgroundSource>();
  const normalizedCredentials = [];

  for (const credential of credentials) {
    const source = resolveLegacyBackground(credentials, credential);
    let backgroundKey = credential?.backgroundKey ?? "";
    if (source) {
      let background = backgroundsBySource.get(source);
      if (!background) {
        background = await backgroundSource(source);
        backgroundsBySource.set(source, background);
      }
      backgroundKey = background.key;
    }

    const normalized = { ...credential, backgroundKey };
    delete normalized.bg;
    delete normalized.posBg;
    normalizedCredentials.push(normalized);
  }

  return { credentials: normalizedCredentials, backgrounds: Array.from(backgroundsBySource.values()) };
}

const toStoredBackground = (background: CredentialBackgroundSource): StoredCredentialBackground => {
  if (background.sourceType === "data-url") {
    const { blob, mimeType } = dataUrlToBlob(background.source);
    return { key: background.key, sourceType: background.sourceType, mimeType, blob, updatedAt: Date.now() };
  }
  return {
    key: background.key,
    sourceType: background.sourceType,
    mimeType: background.mimeType,
    url: background.source,
    updatedAt: Date.now()
  };
};

const saveBackgroundsInIndexedDb = async (backgrounds: CredentialBackgroundSource[]) => {
  const database = await openDatabase();
  const transaction = database.transaction(BACKGROUND_STORE, "readwrite");
  const store = transaction.objectStore(BACKGROUND_STORE);
  backgrounds.forEach(background => store.put(toStoredBackground(background)));
  await waitForTransaction(transaction);

  const verification = database.transaction(BACKGROUND_STORE, "readonly").objectStore(BACKGROUND_STORE);
  const stored = await Promise.all(backgrounds.map(background => requestResult(verification.get(background.key))));
  if (stored.some(value => !value)) throw new Error("Falha ao validar os fundos no IndexedDB");
};

const pruneIndexedDbBackgrounds = async (activeKeys: Set<string>) => {
  const database = await openDatabase();
  const transaction = database.transaction(BACKGROUND_STORE, "readwrite");
  const store = transaction.objectStore(BACKGROUND_STORE);
  const storedKeys = await requestResult<IDBValidKey[]>(store.getAllKeys());
  storedKeys.filter(key => !activeKeys.has(String(key))).forEach(key => store.delete(key));
  await waitForTransaction(transaction);
};

const saveFallbackBackgrounds = (backgrounds: CredentialBackgroundSource[], preserveExisting = true) => {
  const next = preserveExisting ? getStorageData(BACKGROUND_FALLBACK_PROP, {}, true) ?? {} : {};
  backgrounds.forEach(background => {
    next[background.key] = background;
  });
  if (!setStorageData(BACKGROUND_FALLBACK_PROP, next)) {
    throw new Error("Não foi possível salvar os fundos no armazenamento alternativo");
  }
};

export async function persistBackgrounds(backgrounds: CredentialBackgroundSource[]) {
  try {
    await saveBackgroundsInIndexedDb(backgrounds);
    return "indexeddb" as const;
  } catch (error) {
    globalThis.window?.$log?.error?.("Credential backgrounds IndexedDB error.", error);
    saveFallbackBackgrounds(backgrounds);
    return "localstorage" as const;
  }
}

const stableCredential = credential => {
  const persistent = { ...credential };
  delete persistent.shareUrl;
  delete persistent.urlWallet;
  return persistent;
};

export async function persistCredentialBundle(rawCredentials: any[]): Promise<NormalizedCredentialBundle> {
  const normalized = await normalizeCredentials(rawCredentials);
  const storage = await persistBackgrounds(normalized.backgrounds);
  if (!setStorageData(CREDENTIAL_METADATA_PROP, normalized.credentials)) {
    throw new Error("Não foi possível salvar os metadados das credenciais");
  }
  const activeKeys = new Set(normalized.backgrounds.map(background => background.key));
  if (storage === "indexeddb") {
    setStorageData(BACKGROUND_FALLBACK_PROP, null);
    try {
      await pruneIndexedDbBackgrounds(activeKeys);
    } catch (error) {
      globalThis.window?.$log?.error?.("Credential backgrounds cleanup error.", error);
    }
  } else {
    saveFallbackBackgrounds(normalized.backgrounds, false);
  }
  const revision = await sha256(JSON.stringify(normalized.credentials.map(stableCredential)));
  return { schemaVersion: CREDENTIAL_SCHEMA_VERSION, revision, ...normalized, storage };
}

const getBackgroundRecords = async (keys: string[]): Promise<Record<string, StoredCredentialBackground>> => {
  const records: Record<string, StoredCredentialBackground> = {};
  try {
    const database = await openDatabase();
    const store = database.transaction(BACKGROUND_STORE, "readonly").objectStore(BACKGROUND_STORE);
    const values = await Promise.all(keys.map(key => requestResult<StoredCredentialBackground>(store.get(key))));
    values.filter(Boolean).forEach(value => (records[value.key] = value));
  } catch (error) {
    globalThis.window?.$log?.error?.("Credential backgrounds read error.", error);
  }

  const fallback = getStorageData(BACKGROUND_FALLBACK_PROP, {}, true) ?? {};
  keys.forEach(key => {
    if (records[key] || !fallback[key]) return;
    const source = fallback[key] as CredentialBackgroundSource;
    records[key] = toStoredBackground(source);
  });
  return records;
};

export function revokeCredentialBackgroundUrls() {
  Object.values(objectUrls).forEach(url => globalThis.URL?.revokeObjectURL?.(url));
  objectUrls = {};
}

export async function loadCredentialBackgroundUrls(credentials: any[]) {
  revokeCredentialBackgroundUrls();
  const keys = Array.from(new Set(credentials.map(credential => credential.backgroundKey).filter(Boolean)));
  const records = await getBackgroundRecords(keys);
  const urls: Record<string, string> = {};
  keys.forEach(key => {
    const record = records[key];
    if (!record) return;
    if (record.sourceType === "url") {
      urls[key] = record.url ?? "";
    } else if (record.blob) {
      urls[key] = globalThis.URL.createObjectURL(record.blob);
      objectUrls[key] = urls[key];
    }
  });
  return urls;
}

export async function exportCredentialBackgrounds(keys: string[]): Promise<CredentialBackgroundSource[]> {
  const records = await getBackgroundRecords(Array.from(new Set(keys.filter(Boolean))));
  return Promise.all(
    Object.values(records).map(async record => ({
      key: record.key,
      sourceType: record.sourceType,
      mimeType: record.mimeType,
      source: record.sourceType === "url" ? record.url ?? "" : await blobToDataUrl(record.blob)
    }))
  );
}

export async function clearCredentialBackgroundStorage() {
  revokeCredentialBackgroundUrls();
  setStorageData(BACKGROUND_FALLBACK_PROP, null);
  setStorageData(CREDENTIAL_METADATA_PROP, null);
  setStorageData(LEGACY_CREDENTIAL_PROP, null);
  try {
    const database = await openDatabase();
    const transaction = database.transaction(BACKGROUND_STORE, "readwrite");
    transaction.objectStore(BACKGROUND_STORE).clear();
    await waitForTransaction(transaction);
  } catch (error) {
    globalThis.window?.$log?.error?.("Credential backgrounds cleanup error.", error);
  }
}

export function getStoredCredentialMetadata() {
  const metadata = getStorageData(CREDENTIAL_METADATA_PROP, null, true);
  if (Array.isArray(metadata)) return metadata;
  return getStorageData(LEGACY_CREDENTIAL_PROP, [], true);
}
